import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { api, getErrorMessage } from '../lib/api';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useAuthStore } from '../stores/authStore';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { LoadingState } from '../components/ui/LoadingState';
import {
  Search,
  AlertTriangle,
  Key,
  ArrowRight,
  Settings,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Calendar,
  ShieldCheck,
  CheckCircle2,
  Copy,
} from 'lucide-react';
import { EmptyState } from '../components/ui/EmptyState';
import type { Application, ApplicationWithSecrets } from '../types';

export const Applications: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { environment } = useEnvironment();
  const client = useAuthStore((state) => state.client);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [newAppDescription, setNewAppDescription] = useState('');
  const [createdApp, setCreatedApp] = useState<ApplicationWithSecrets | null>(null);

  // Filters and Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(6);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setOffset(0);
  }, [environment, limit]);

  const { data, isLoading, isFetching } = useQuery<{
    data: Application[];
    meta: { total: number };
  }>({
    queryKey: ['applications', environment, client?.id, debouncedSearch, offset, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      params.append('sandbox_mode', (environment === 'sandbox').toString());

      const response = await api.get<{
        success: boolean;
        data: Application[];
        meta: { total: number };
      }>(`/clients/${client?.id}/applications?${params.toString()}`);

      return response.data;
    },
    enabled: !!client?.id,
    placeholderData: (prev) => prev,
  });

  const applications = data?.data || [];
  const total = data?.meta?.total || 0;
  const totalPages = Math.ceil(total / limit) || 1;
  const currentPage = Math.floor(offset / limit) + 1;

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<ApplicationWithSecrets>(
        `/clients/${client?.id}/applications`,
        {
          name: newAppName,
          description: newAppDescription,
          sandbox_mode: environment === 'sandbox',
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setCreatedApp(data);
      void queryClient.invalidateQueries({ queryKey: ['applications', environment] });
      setNewAppName('');
      setNewAppDescription('');
    },
    onError: (error) => {
      void Swal.fire('Erro', getErrorMessage(error), 'error');
    },
  });

  const handleDelete = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    void Swal.fire({
      title: 'Tem a certeza?',
      text: `Deseja eliminar a aplicação "${name}"? Esta ação não pode ser desfeita e revogará todos os acessos.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, eliminar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        api
          .delete(`/clients/${client?.id}/applications/${id}`)
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['applications', environment] });
            void Swal.fire('Sucesso', 'Aplicação eliminada com sucesso', 'success');
          })
          .catch((error) => {
            void Swal.fire('Erro', getErrorMessage(error), 'error');
          });
      }
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    void Swal.fire({
      title: 'Copiado!',
      text: `${label} copiado para a área de transferência`,
      icon: 'success',
      timer: 1500,
      showConfirmButton: false,
    });
  };

  return (
    <div className="container-custom py-8 space-y-8 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Dashboard</BreadcrumbItem>
        <BreadcrumbItem>Aplicações</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Aplicações</h1>
          <p className="text-slate-600 dark:text-slate-300">
            Crie e gira aplicações no ambiente{' '}
            {environment === 'sandbox' ? 'de Sandbox' : 'de Produção'}.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-base btn-primary px-6 py-3 shadow-md hover:shadow-lg flex items-center gap-2"
        >
          <PlusCircle className="w-5 h-5" /> Nova Aplicação
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Pesquisar por nome da aplicação..."
            className="w-full pl-10 pr-10 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 transition-colors"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              title="Limpar pesquisa"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      {isLoading ? (
        <LoadingState type="card" count={3} />
      ) : applications && applications.length > 0 ? (
        <>
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-200 ${isFetching ? 'opacity-60' : 'opacity-100'}`}
          >
            {applications.map((app) => (
              <div
                key={app.id}
                onClick={() => navigate(`/applications/${app.id}`)}
                className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer group flex flex-col h-full"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-xl flex items-center justify-center">
                    <Key className="w-6 h-6" />
                  </div>
                  <span
                    className={`badge-modern ${app.status === 'active' ? 'badge-success' : 'badge-danger'}`}
                  >
                    {app.status === 'active' ? 'Ativa' : 'Inativa'}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {app.name}
                </h3>

                <div className="space-y-2 mb-6 flex-grow">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      {app.sandbox_mode ? 'Sandbox (Testes)' : 'Produção'}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>Criada em {new Date(app.created_at).toLocaleDateString('pt-PT')}</span>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
                    <span>
                      {app.ip_whitelist && app.ip_whitelist.length > 0
                        ? `${app.ip_whitelist.length} IP(s) autorizado(s)`
                        : 'Acesso de qualquer IP'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800 mt-auto">
                  <button
                    onClick={(e) => handleDelete(app.id, app.name, e)}
                    className="text-slate-500 dark:text-slate-300 hover:text-error-500 dark:hover:text-error-400 text-sm font-medium transition-colors px-2 py-1"
                  >
                    Eliminar
                  </button>
                  <div className="flex items-center gap-1 text-primary-600 dark:text-primary-400 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    <Settings className="w-4 h-4" /> Gerir <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {total > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex items-center gap-4 flex-wrap">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  A mostrar{' '}
                  <span className="font-bold text-slate-900 dark:text-white">{offset + 1}</span> a{' '}
                  <span className="font-bold text-slate-900 dark:text-white">
                    {Math.min(offset + limit, total)}
                  </span>{' '}
                  de <span className="font-bold text-slate-900 dark:text-white">{total}</span>{' '}
                  aplicações
                </p>

                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span>Por página:</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white px-2 py-1 text-xs focus:ring-2 focus:ring-primary-500 outline-none"
                  >
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3.5 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" /> Anterior
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 text-sm">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setOffset(((p as number) - 1) * limit)}
                          className={`min-w-[38px] h-[38px] rounded-xl text-sm font-bold transition-all ${
                            currentPage === p
                              ? 'bg-primary-600 text-white shadow-md'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}

                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="px-3.5 py-2 rounded-xl text-sm font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                  >
                    Próxima <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyState
          title="Nenhuma aplicação encontrada"
          description={
            searchTerm
              ? 'Tente ajustar os filtros de pesquisa.'
              : 'A sua jornada começa aqui. Crie a sua primeira aplicação para explorar o nosso catálogo de APIs.'
          }
          action={
            !searchTerm ? (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-base btn-primary px-8 py-3 text-lg shadow-lg"
              >
                <PlusCircle className="w-5 h-5 mr-2" /> Criar a Minha Primeira Aplicação
              </button>
            ) : undefined
          }
        />
      )}

      {/* Created App Modal */}
      {createdApp && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative border border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2.5">
              <CheckCircle2 className="w-7 h-7 text-emerald-500 shrink-0" />
              <span>Aplicação Criada com Sucesso!</span>
            </h2>
            <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800/50 rounded-xl p-4 mb-6">
              <p className="text-warning-800 dark:text-warning-400 text-sm font-medium flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>
                  Importante: Guarde estas credenciais agora em segurança. O Client Secret e a API
                  Key não voltarão a ser exibidos.
                </span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Client ID
                </label>
                <div className="flex gap-2">
                  <code className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 p-3 rounded-lg text-sm border border-slate-200 dark:border-slate-700">
                    {createdApp.client_id_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(createdApp.client_id_key, 'Client ID')}
                    className="p-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                    title="Copiar Client ID"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Client Secret
                </label>
                <div className="flex gap-2">
                  <code className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 p-3 rounded-lg text-sm border border-slate-200 dark:border-slate-700">
                    {createdApp.client_secret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(createdApp.client_secret, 'Client Secret')}
                    className="p-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                    title="Copiar Client Secret"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  API Key
                </label>
                <div className="flex gap-2">
                  <code className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 p-3 rounded-lg text-sm border border-slate-200 dark:border-slate-700">
                    {createdApp.api_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(createdApp.api_key, 'API Key')}
                    className="p-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                    title="Copiar API Key"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setCreatedApp(null);
                navigate(`/applications/${createdApp.id}`);
              }}
              className="mt-8 w-full btn-base btn-primary py-4 text-lg shadow-lg"
            >
              Já guardei as credenciais. Aceder à Aplicação
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              Nova Aplicação
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate();
              }}
              className="space-y-4"
            >
              <div>
                <label className="label-modern dark:text-slate-300">Nome da Aplicação</label>
                <input
                  type="text"
                  required
                  className="input-modern dark:bg-slate-800 dark:text-white dark:border-slate-700"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  placeholder="Ex: App de Pagamentos"
                />
              </div>
              <div>
                <label className="label-modern dark:text-slate-300">Descrição (Opcional)</label>
                <textarea
                  className="input-modern dark:bg-slate-800 dark:text-white dark:border-slate-700 min-h-[100px]"
                  value={newAppDescription}
                  onChange={(e) => setNewAppDescription(e.target.value)}
                  placeholder="Para que serve esta aplicação?"
                />
              </div>
              <div className="flex gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 btn-base bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 btn-base btn-primary"
                >
                  {createMutation.isPending ? 'A criar...' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
